import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Box, Button, Chip, CircularProgress, FormControlLabel, IconButton, MenuItem, Rating,
  Select, Stack, Switch, Tab, Tabs, TextField, Tooltip, Typography, alpha,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core'
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import TextFieldsOutlinedIcon from '@mui/icons-material/TextFieldsOutlined'
import NumbersOutlinedIcon from '@mui/icons-material/NumbersOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ThumbsUpDownOutlinedIcon from '@mui/icons-material/ThumbsUpDownOutlined'
import RadioButtonCheckedOutlinedIcon from '@mui/icons-material/RadioButtonCheckedOutlined'
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined'
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined'
import StarOutlineIcon from '@mui/icons-material/StarOutline'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import CloudDoneOutlinedIcon from '@mui/icons-material/CloudDoneOutlined'
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded'
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined'
import MicNoneRoundedIcon from '@mui/icons-material/MicNoneRounded'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined'
import PublishOutlinedIcon from '@mui/icons-material/PublishOutlined'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined'
import CloseIcon from '@mui/icons-material/Close'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError } from '@cmms/api-client'
import type { ChecklistTemplate, ChecklistSection, ChecklistItem, ChecklistItemType } from '@cmms/types'

import { api } from '../../lib/api'
import { toast } from '../../store/snackbarStore'
import { useOrgSettings } from '../../hooks/useOrgSettings'
import { SearchableSelect } from '../../components/SearchableSelect'

const ITEM_TYPES: { type: ChecklistItemType; label: string; icon: React.ReactNode; desc: string }[] = [
  { type: 'text',            label: 'Text',            icon: <TextFieldsOutlinedIcon sx={{ fontSize: 18 }} />, desc: 'Free text answer' },
  { type: 'number',          label: 'Number',          icon: <NumbersOutlinedIcon sx={{ fontSize: 18 }} />,    desc: 'Numeric value' },
  { type: 'yes_no',          label: 'Yes / No',        icon: <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />, desc: 'Simple yes or no' },
  { type: 'pass_fail',       label: 'Pass / Fail',     icon: <ThumbsUpDownOutlinedIcon sx={{ fontSize: 18 }} />, desc: 'Pass or fail check' },
  { type: 'multiple_choice', label: 'Single select',     icon: <RadioButtonCheckedOutlinedIcon sx={{ fontSize: 18 }} />, desc: 'Pick one from a list' },
  { type: 'checkbox',        label: 'Multi select',      icon: <CheckBoxOutlinedIcon sx={{ fontSize: 18 }} />,  desc: 'Pick one or more' },
  { type: 'date',            label: 'Date',             icon: <CalendarMonthOutlinedIcon sx={{ fontSize: 18 }} />, desc: 'Date picker' },
  { type: 'rating',          label: 'Rating',           icon: <StarOutlineIcon sx={{ fontSize: 18 }} />,       desc: '1 to 5 stars' },
  { type: 'instruction',     label: 'Instruction',      icon: <InfoOutlinedIcon sx={{ fontSize: 18 }} />,      desc: 'Read-only note' },
]

interface Props {
  templateId: string
  editable: boolean
}

export function ChecklistBuilder({ templateId, editable }: Props) {
  const qc = useQueryClient()
  const [saving, setSaving] = useState(false)
  const [lastSaved, setLastSaved] = useState<Date | null>(null)
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set())
  const [view, setView] = useState<'edit' | 'preview' | 'history'>('edit')

  const templateQ = useQuery({
    queryKey: ['checklist', templateId],
    queryFn: () => api.checklists.get(templateId),
  })
  const template: ChecklistTemplate | null = templateQ.data ?? null

  useEffect(() => {
    if (template?.sections) setExpandedSections(new Set(template.sections.map((s) => s.id)))
  }, [template?.id])

  const refreshTemplate = useCallback(() => {
    qc.invalidateQueries({ queryKey: ['checklist', templateId] })
    qc.invalidateQueries({ queryKey: ['checklists'] })
    setLastSaved(new Date())
  }, [qc, templateId])

  const addSectionM = useMutation({
    mutationFn: () => api.checklists.createSection(templateId, { title: 'New section' }),
    onSuccess: (s) => { refreshTemplate(); setExpandedSections((prev) => new Set([...prev, s.id])) },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  const deleteSectionM = useMutation({
    mutationFn: (sectionId: string) => api.checklists.deleteSection(templateId, sectionId),
    onSuccess: () => refreshTemplate(),
  })

  // Add item — if no sections exist, auto-create one first
  async function handleAddItem(type: ChecklistItemType, sectionId?: string) {
    let targetSectionId = sectionId
    if (!targetSectionId) {
      if (template?.sections.length) {
        targetSectionId = template.sections[template.sections.length - 1]!.id
      } else {
        const section = await api.checklists.createSection(templateId, { title: 'General' })
        targetSectionId = section.id
        setExpandedSections((prev) => new Set([...prev, section.id]))
      }
    }
    await api.checklists.createItem(templateId, targetSectionId, {
      type,
      label: type === 'instruction' ? 'Enter instruction text here' : '',
    })
    refreshTemplate()
  }

  const deleteItemM = useMutation({
    mutationFn: ({ sectionId, itemId }: { sectionId: string; itemId: string }) =>
      api.checklists.deleteItem(templateId, sectionId, itemId),
    onSuccess: () => refreshTemplate(),
  })

  // Reorder is optimistic: we apply the new order to the React Query
  // cache up-front so the dropped row stays where the user put it, then
  // fire the API in the background. If the request fails we restore the
  // snapshot so the UI doesn't drift from the server.
  async function handleReorderSections(fromIndex: number, toIndex: number) {
    if (!template || fromIndex === toIndex) return
    const sections = [...template.sections]
    const [moved] = sections.splice(fromIndex, 1)
    sections.splice(toIndex, 0, moved!)
    const order = sections.map((s, i) => ({ id: s.id, position: i }))
    const queryKey = ['checklist', templateId] as const
    const snapshot = qc.getQueryData<ChecklistTemplate>(queryKey)
    qc.setQueryData<ChecklistTemplate>(queryKey, (old) =>
      old ? { ...old, sections: sections.map((s, i) => ({ ...s, position: i })) } : old
    )
    setSaving(true)
    try {
      await api.checklists.reorderSections(templateId, { order })
      setLastSaved(new Date())
    } catch (e) {
      qc.setQueryData(queryKey, snapshot)
      toast.error(e instanceof ApiError ? e.message : 'Reorder failed')
    } finally {
      setSaving(false)
    }
  }

  async function handleReorderItems(sectionId: string, fromIndex: number, toIndex: number) {
    if (!template || fromIndex === toIndex) return
    const section = template.sections.find((s) => s.id === sectionId)
    if (!section) return
    const items = [...section.items]
    const [moved] = items.splice(fromIndex, 1)
    items.splice(toIndex, 0, moved!)
    const order = items.map((it, i) => ({ id: it.id, position: i }))
    const queryKey = ['checklist', templateId] as const
    const snapshot = qc.getQueryData<ChecklistTemplate>(queryKey)
    qc.setQueryData<ChecklistTemplate>(queryKey, (old) => {
      if (!old) return old
      return {
        ...old,
        sections: old.sections.map((s) =>
          s.id === sectionId
            ? { ...s, items: items.map((it, i) => ({ ...it, position: i })) }
            : s
        ),
      }
    })
    setSaving(true)
    try {
      await api.checklists.reorderItems(templateId, sectionId, { order })
      setLastSaved(new Date())
    } catch (e) {
      qc.setQueryData(queryKey, snapshot)
      toast.error(e instanceof ApiError ? e.message : 'Reorder failed')
    } finally {
      setSaving(false)
    }
  }

  function toggleSection(id: string) {
    setExpandedSections((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n })
  }

  if (templateQ.isLoading || !template) {
    return <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress size={24} /></Box>
  }

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      {/* Toolbar */}
      <Stack direction="row" alignItems="center" justifyContent="space-between"
        sx={{ px: 3, py: 1.25, borderBottom: '1px solid', borderColor: 'divider',
          bgcolor: (t) => t.palette.mode === 'light' ? '#FAFAFB' : 'rgba(255,255,255,0.02)' }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          {saving ? (
            <><CircularProgress size={14} /><Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Saving...</Typography></>
          ) : lastSaved ? (
            <><CloudDoneOutlinedIcon sx={{ fontSize: 14, color: '#059669' }} /><Typography sx={{ fontSize: 12, color: '#059669', fontWeight: 500 }}>Saved</Typography></>
          ) : null}
          {!editable && (
            <Chip label="Read-only" size="small" sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: 'action.hover', color: 'text.secondary' }} />
          )}
        </Stack>
        <Tabs value={view} onChange={(_, v) => setView(v)}
          sx={{ minHeight: 32, '& .MuiTab-root': { minHeight: 32, textTransform: 'none', fontSize: 12, fontWeight: 600, px: 1.5, py: 0 },
            '& .MuiTabs-indicator': { height: 2 } }}>
          <Tab value="edit" icon={<EditOutlinedIcon sx={{ fontSize: 14 }} />} iconPosition="start" label="Build" />
          <Tab value="preview" icon={<VisibilityOutlinedIcon sx={{ fontSize: 14 }} />} iconPosition="start" label="Preview" />
          <Tab value="history" icon={<HistoryOutlinedIcon sx={{ fontSize: 14 }} />} iconPosition="start" label="History" />
        </Tabs>
      </Stack>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', scrollbarWidth: 'thin' }}>
        {view === 'edit' && (
          <BuildView template={template} templateId={templateId} editable={editable}
            expandedSections={expandedSections} toggleSection={toggleSection}
            onAddSection={() => addSectionM.mutate()} addingSection={addSectionM.isPending}
            onDeleteSection={(id) => deleteSectionM.mutate(id)}
            onAddItem={handleAddItem}
            onDeleteItem={(sid, iid) => deleteItemM.mutate({ sectionId: sid, itemId: iid })}
            onReorderSections={handleReorderSections}
            onReorderItems={handleReorderItems}
            onSaving={setSaving} onSaved={refreshTemplate} />
        )}
        {view === 'preview' && <PreviewView template={template} />}
        {view === 'history' && <HistoryView template={template} />}
      </Box>
    </Box>
  )
}

// ═══════════════════════════════════════════════════════════════════
// BUILD VIEW
// ═══════════════════════════════════════════════════════════════════

function BuildView({ template, templateId, editable, expandedSections, toggleSection, onAddSection, addingSection, onDeleteSection, onAddItem, onDeleteItem, onReorderSections, onReorderItems, onSaving, onSaved }: {
  template: ChecklistTemplate; templateId: string; editable: boolean
  expandedSections: Set<string>; toggleSection: (id: string) => void
  onAddSection: () => void; addingSection: boolean
  onDeleteSection: (id: string) => void
  onAddItem: (type: ChecklistItemType, sectionId?: string) => void
  onDeleteItem: (sectionId: string, itemId: string) => void
  onReorderSections: (from: number, to: number) => void
  onReorderItems: (sectionId: string, from: number, to: number) => void
  onSaving: (v: boolean) => void; onSaved: () => void
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  function handleSectionDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIdx = template.sections.findIndex((s) => s.id === active.id)
    const newIdx = template.sections.findIndex((s) => s.id === over.id)
    if (oldIdx !== -1 && newIdx !== -1) onReorderSections(oldIdx, newIdx)
  }
  return (
    <Box sx={{ px: 3, py: 3, maxWidth: 800, mx: 'auto' }}>
      {/* Quick add bar — add question without a section */}
      {editable && (
        <Box sx={{ mb: 3, p: 2.5, border: '2px dashed', borderColor: 'divider', borderRadius: 2,
          transition: 'border-color 0.15s', '&:hover': { borderColor: 'primary.main' } }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', mb: 1.5 }}>
            Add a question
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {ITEM_TYPES.map((t) => (
              <Tooltip key={t.type} title={t.desc} arrow placement="top">
                <Button size="small" startIcon={t.icon} onClick={() => onAddItem(t.type)}
                  variant="outlined"
                  sx={{ fontSize: 12, fontWeight: 600, textTransform: 'none', borderColor: 'divider', color: 'text.secondary',
                    px: 1.5, '&:hover': { color: 'primary.main', borderColor: 'primary.main' } }}>
                  {t.label}
                </Button>
              </Tooltip>
            ))}
          </Stack>
        </Box>
      )}

      {/* Sections — sortable */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleSectionDragEnd}>
        <SortableContext items={template.sections.map((s) => s.id)} strategy={verticalListSortingStrategy}>
          {template.sections.map((section) => (
            <SortableSectionWrapper key={section.id} id={section.id} editable={editable}>
              <SectionBlock section={section} templateId={templateId} editable={editable}
                expanded={expandedSections.has(section.id)}
                allTemplateItems={template.sections.flatMap((s) => s.items)}
                onToggle={() => toggleSection(section.id)}
                onDeleteSection={() => onDeleteSection(section.id)}
                onAddItem={(type) => onAddItem(type, section.id)}
                onDeleteItem={(itemId) => onDeleteItem(section.id, itemId)}
                onReorderItems={(from, to) => onReorderItems(section.id, from, to)}
                onSaving={onSaving} onSaved={onSaved} />
            </SortableSectionWrapper>
          ))}
        </SortableContext>
      </DndContext>

      {editable && (
        <Button startIcon={<AddIcon sx={{ fontSize: 16 }} />} onClick={onAddSection} disabled={addingSection}
          sx={{ mt: 2, fontSize: 13, fontWeight: 600, color: 'primary.main' }}>
          Add section
        </Button>
      )}

      {template.sections.length === 0 && !editable && (
        <Box sx={{ py: 8, textAlign: 'center' }}>
          <Typography sx={{ fontSize: 15, color: 'text.secondary' }}>This checklist is empty</Typography>
        </Box>
      )}
    </Box>
  )
}

function SortableSectionWrapper({ id, editable, children }: { id: string; editable: boolean; children: React.ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled: !editable })
  return (
    <Box ref={setNodeRef} {...attributes}
      sx={{ transform: CSS.Transform.toString(transform), transition: transition ?? 'transform 200ms ease',
        opacity: isDragging ? 0.5 : 1, position: 'relative', zIndex: isDragging ? 10 : 'auto',
        '& > *:first-of-type': { cursor: editable ? 'default' : 'auto' } }}>
      {editable && (
        <Box {...listeners} sx={{ position: 'absolute', left: -28, top: 16, cursor: 'grab', zIndex: 2, color: 'text.disabled',
          '&:hover': { color: 'text.secondary' }, '&:active': { cursor: 'grabbing' } }}>
          <DragIndicatorIcon sx={{ fontSize: 20 }} />
        </Box>
      )}
      {children}
    </Box>
  )
}

function SortableItemWrapper({ id, editable, children }: { id: string; editable: boolean; children: React.ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled: !editable })
  return (
    <Box ref={setNodeRef} {...attributes}
      sx={{ transform: CSS.Transform.toString(transform), transition: transition ?? 'transform 200ms ease',
        opacity: isDragging ? 0.45 : 1, position: 'relative', zIndex: isDragging ? 10 : 'auto',
        bgcolor: isDragging ? 'background.paper' : 'transparent',
        boxShadow: isDragging ? '0 8px 24px -4px rgba(0,0,0,0.15)' : 'none',
        borderRadius: isDragging ? 2 : 0 }}>
      {editable && (
        <Box {...listeners} sx={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', cursor: 'grab', zIndex: 2, color: 'text.disabled',
          '&:hover': { color: 'text.secondary' }, '&:active': { cursor: 'grabbing' } }}>
          <DragIndicatorIcon sx={{ fontSize: 18 }} />
        </Box>
      )}
      {children}
    </Box>
  )
}

function SectionBlock({ section, templateId, editable, expanded, allTemplateItems, onToggle, onDeleteSection, onAddItem, onDeleteItem, onReorderItems, onSaving, onSaved }: {
  section: ChecklistSection; templateId: string; editable: boolean; expanded: boolean
  allTemplateItems: ChecklistItem[]
  onToggle: () => void; onDeleteSection: () => void
  onAddItem: (type: ChecklistItemType) => void; onDeleteItem: (itemId: string) => void
  onReorderItems: (from: number, to: number) => void
  onSaving: (v: boolean) => void; onSaved: () => void
}) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))
  const [title, setTitle] = useState(section.title)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  function handleTitleChange(value: string) {
    setTitle(value)
    if (!editable) return
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(async () => {
      onSaving(true)
      try { await api.checklists.updateSection(templateId, section.id, { title: value.trim() }); onSaved() }
      catch {} finally { onSaving(false) }
    }, 800)
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setTitle(section.title) }, [section.id])

  return (
    <Box sx={{ mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2.5, overflow: 'hidden',
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
      {/* Section header */}
      <Stack direction="row" alignItems="center" spacing={1.5}
        sx={{ px: 3, py: 2, cursor: 'pointer',
          bgcolor: (t) => alpha(t.palette.primary.main, t.palette.mode === 'light' ? 0.03 : 0.06),
          borderBottom: expanded ? '1px solid' : 'none', borderColor: 'divider',
          '&:hover': { bgcolor: (t) => alpha(t.palette.primary.main, t.palette.mode === 'light' ? 0.06 : 0.1) } }}
        onClick={onToggle}>
        {expanded ? <ExpandLessIcon sx={{ fontSize: 20, color: 'text.secondary' }} /> : <ExpandMoreIcon sx={{ fontSize: 20, color: 'text.secondary' }} />}
        {editable ? (
          <TextField value={title} onChange={(e) => handleTitleChange(e.target.value)} size="small" variant="standard"
            onClick={(e) => e.stopPropagation()} placeholder="Section title"
            InputProps={{ disableUnderline: true, sx: { fontSize: 18, fontWeight: 700 } }}
            sx={{ flex: 1 }} />
        ) : (
          <Typography sx={{ fontSize: 18, fontWeight: 700, flex: 1 }}>{section.title}</Typography>
        )}
        <Chip label={`${section.items.length} item${section.items.length === 1 ? '' : 's'}`} size="small"
          sx={{ height: 22, fontSize: 11, fontWeight: 600, bgcolor: 'action.hover', color: 'text.secondary' }} />
        {editable && (
          <Tooltip title="Delete section and all its items" arrow>
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDeleteSection() }}
              sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}>
              <DeleteOutlineIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      {expanded && (
        <>
          <DndContext sensors={sensors} collisionDetection={closestCenter}
            onDragEnd={(event) => {
              const { active, over } = event
              if (!over || active.id === over.id) return
              const oldIdx = section.items.findIndex((it) => it.id === active.id)
              const newIdx = section.items.findIndex((it) => it.id === over.id)
              if (oldIdx !== -1 && newIdx !== -1) onReorderItems(oldIdx, newIdx)
            }}>
            <SortableContext items={section.items.map((it) => it.id)} strategy={verticalListSortingStrategy}>
              {section.items.map((item) => (
                <SortableItemWrapper key={item.id} id={item.id} editable={editable}>
                  <ItemRow item={item} templateId={templateId} sectionId={section.id}
                    editable={editable} allTemplateItems={allTemplateItems}
                    onDelete={() => onDeleteItem(item.id)}
                    onSaving={onSaving} onSaved={onSaved} />
                </SortableItemWrapper>
              ))}
            </SortableContext>
          </DndContext>

          {editable && (
            <Box sx={{ px: 3, py: 2, borderTop: section.items.length > 0 ? '1px solid' : 'none', borderColor: 'divider',
              bgcolor: (t) => t.palette.mode === 'light' ? '#FAFAFB' : 'rgba(255,255,255,0.01)' }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.disabled', mb: 1 }}>
                Add to this section
              </Typography>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                {ITEM_TYPES.map((t) => (
                  <Tooltip key={t.type} title={t.desc} arrow placement="top">
                    <IconButton size="small" onClick={() => onAddItem(t.type)}
                      sx={{ width: 32, height: 32, border: '1px solid', borderColor: 'divider', borderRadius: 1.5,
                        color: 'text.secondary', '&:hover': { color: 'primary.main', borderColor: 'primary.main' } }}>
                      {t.icon}
                    </IconButton>
                  </Tooltip>
                ))}
              </Stack>
            </Box>
          )}

          {section.items.length === 0 && !editable && (
            <Box sx={{ px: 3, py: 3, textAlign: 'center' }}>
              <Typography sx={{ fontSize: 13, color: 'text.disabled' }}>No items in this section</Typography>
            </Box>
          )}
        </>
      )}
    </Box>
  )
}

function ItemRow({ item, templateId, sectionId, editable, allTemplateItems, onDelete, onSaving, onSaved }: {
  item: ChecklistItem; templateId: string; sectionId: string; editable: boolean
  allTemplateItems: ChecklistItem[]
  onDelete: () => void; onSaving: (v: boolean) => void; onSaved: () => void
}) {
  const [label, setLabel] = useState(item.label)
  const [required, setRequired] = useState(item.required)
  const [showCondition, setShowCondition] = useState(!!item.condition)
  const [type, setType] = useState(item.type)
  const [options, setOptions] = useState<string[]>(item.options ?? [])
  // Evidence-gate flags. Each one independently blocks submission of
  // the checklist response until the matching evidence is attached.
  const [requireComment, setRequireComment] = useState(item.require_comment ?? false)
  const [requireImage,   setRequireImage]   = useState(item.require_image   ?? false)
  const [requireVoice,   setRequireVoice]   = useState(item.require_voice   ?? false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setLabel(item.label)
    setRequired(item.required)
    setType(item.type)
    setOptions(item.options ?? [])
    setRequireComment(item.require_comment ?? false)
    setRequireImage(item.require_image ?? false)
    setRequireVoice(item.require_voice ?? false)
  }, [item.id])

  function autoSave(fields: Record<string, any>) {
    if (!editable) return
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(async () => {
      onSaving(true)
      try { await api.checklists.updateItem(templateId, sectionId, item.id, fields); onSaved() }
      catch {} finally { onSaving(false) }
    }, 600)
  }

  function handleTypeChange(newType: ChecklistItemType) {
    setType(newType)
    const fields: any = { type: newType }
    if ((newType === 'multiple_choice' || newType === 'checkbox') && options.length === 0) {
      const defaults = ['Option 1', 'Option 2']
      setOptions(defaults)
      fields.options = defaults
    }
    autoSave(fields)
  }

  function handleOptionChange(index: number, value: string) {
    const next = [...options]; next[index] = value; setOptions(next); autoSave({ options: next })
  }
  function addOption() {
    const next = [...options, `Option ${options.length + 1}`]; setOptions(next); autoSave({ options: next })
  }
  function removeOption(index: number) {
    const next = options.filter((_, i) => i !== index); setOptions(next); autoSave({ options: next })
  }

  const typeInfo = ITEM_TYPES.find((t) => t.type === type)
  const hasOptions = type === 'multiple_choice' || type === 'checkbox'
  const isInstruction = type === 'instruction'

  return (
    <Box sx={{ px: 3, py: 3, borderBottom: '1px solid', borderColor: 'divider',
      '&:last-of-type': { borderBottom: 'none' },
      transition: 'background-color 0.12s',
      '&:hover': { bgcolor: (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.008)' : 'rgba(255,255,255,0.008)' } }}>
      <Stack direction="row" alignItems="flex-start" spacing={2} sx={{ pl: editable ? 3.5 : 0 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Top row: type selector + required toggle */}
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
            {editable ? (
              <Select value={type} onChange={(e) => handleTypeChange(e.target.value as ChecklistItemType)} size="small" variant="outlined"
                sx={{ height: 30, fontSize: 12, fontWeight: 600, borderRadius: 1.5, '& .MuiSelect-select': { py: 0.5, display: 'flex', alignItems: 'center', gap: 0.75 } }}>
                {ITEM_TYPES.map((t) => (
                  <MenuItem key={t.type} value={t.type} sx={{ fontSize: 12.5 }}>
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <Box sx={{ display: 'flex', color: 'text.secondary' }}>{t.icon}</Box>
                      <span>{t.label}</span>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Chip icon={typeInfo?.icon as any} label={typeInfo?.label} size="small"
                sx={{ height: 24, fontSize: 11, fontWeight: 600, bgcolor: 'action.hover', color: 'text.secondary' }} />
            )}

            {!isInstruction && (
              editable ? (
                <FormControlLabel
                  control={<Switch size="small" checked={required} onChange={(e) => { setRequired(e.target.checked); autoSave({ required: e.target.checked }) }} />}
                  label={<Typography sx={{ fontSize: 12, fontWeight: 600, color: required ? 'primary.main' : 'text.secondary' }}>Required</Typography>}
                  sx={{ ml: 0 }}
                />
              ) : required ? (
                <Chip label="Required" size="small"
                  sx={{ height: 22, fontSize: 10, fontWeight: 700, bgcolor: (t) => `${t.palette.primary.main}15`, color: 'primary.main', border: '1px solid', borderColor: 'primary.main' }} />
              ) : null
            )}

            {item.condition && (
              <Chip label="Conditional" size="small"
                sx={{ height: 22, fontSize: 10, fontWeight: 700, bgcolor: (t) => `${t.palette.warning.main}15`, color: 'warning.main' }} />
            )}
          </Stack>

          {/* Evidence-gate toggles. Each one independently blocks
              submission of the response until the matching evidence
              (comment / image / voice note) is attached to this
              question. Hidden on instruction-type rows (they're not
              answered, so requiring evidence is meaningless). */}
          {!isInstruction && (
            <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" useFlexGap
              sx={{ mb: 1.5, rowGap: 0.5 }}>
              {editable ? (
                <>
                  <FormControlLabel
                    control={<Switch size="small" checked={requireComment}
                      onChange={(e) => { setRequireComment(e.target.checked); autoSave({ require_comment: e.target.checked }) }} />}
                    label={
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <ChatBubbleOutlineRoundedIcon sx={{ fontSize: 13, color: requireComment ? 'primary.main' : 'text.secondary' }} />
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: requireComment ? 'primary.main' : 'text.secondary' }}>Require comment</Typography>
                      </Stack>
                    }
                    sx={{ ml: 0 }}
                  />
                  <FormControlLabel
                    control={<Switch size="small" checked={requireImage}
                      onChange={(e) => { setRequireImage(e.target.checked); autoSave({ require_image: e.target.checked }) }} />}
                    label={
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <ImageOutlinedIcon sx={{ fontSize: 13, color: requireImage ? 'primary.main' : 'text.secondary' }} />
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: requireImage ? 'primary.main' : 'text.secondary' }}>Require image</Typography>
                      </Stack>
                    }
                    sx={{ ml: 0 }}
                  />
                  <FormControlLabel
                    control={<Switch size="small" checked={requireVoice}
                      onChange={(e) => { setRequireVoice(e.target.checked); autoSave({ require_voice: e.target.checked }) }} />}
                    label={
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <MicNoneRoundedIcon sx={{ fontSize: 13, color: requireVoice ? 'primary.main' : 'text.secondary' }} />
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: requireVoice ? 'primary.main' : 'text.secondary' }}>Require voice note</Typography>
                      </Stack>
                    }
                    sx={{ ml: 0 }}
                  />
                </>
              ) : (
                <>
                  {requireComment && (
                    <Chip icon={<ChatBubbleOutlineRoundedIcon sx={{ fontSize: 12 }} />} label="Comment required" size="small"
                      sx={{ height: 22, fontSize: 10, fontWeight: 700, bgcolor: (t) => `${t.palette.primary.main}12`, color: 'primary.main' }} />
                  )}
                  {requireImage && (
                    <Chip icon={<ImageOutlinedIcon sx={{ fontSize: 12 }} />} label="Image required" size="small"
                      sx={{ height: 22, fontSize: 10, fontWeight: 700, bgcolor: (t) => `${t.palette.primary.main}12`, color: 'primary.main' }} />
                  )}
                  {requireVoice && (
                    <Chip icon={<MicNoneRoundedIcon sx={{ fontSize: 12 }} />} label="Voice required" size="small"
                      sx={{ height: 22, fontSize: 10, fontWeight: 700, bgcolor: (t) => `${t.palette.primary.main}12`, color: 'primary.main' }} />
                  )}
                </>
              )}
            </Stack>
          )}

          {/* Label */}
          {editable ? (
            <TextField value={label} onChange={(e) => { setLabel(e.target.value); autoSave({ label: e.target.value.trim() }) }}
              size="small" fullWidth variant="outlined"
              placeholder={isInstruction ? 'Enter instruction text...' : 'Type your question here...'}
              sx={{ '& .MuiOutlinedInput-root': {
                fontSize: 17, fontWeight: isInstruction ? 400 : 600,
                fontStyle: isInstruction ? 'italic' : 'normal',
                '& fieldset': { borderColor: 'divider' },
              } }} />
          ) : (
            <Typography sx={{ fontSize: 17, fontWeight: isInstruction ? 400 : 600, fontStyle: isInstruction ? 'italic' : 'normal' }}>
              {item.label || '(empty)'}
            </Typography>
          )}

          {/* Options for multiple choice / checkbox */}
          {hasOptions && (
            <Stack spacing={1} sx={{ mt: 2, pl: 0.5 }}>
              {options.map((opt, i) => (
                <Stack key={i} direction="row" alignItems="center" spacing={1.5}>
                  <Box sx={{ width: 18, height: 18, borderRadius: type === 'checkbox' ? 0.5 : 99, border: '2px solid', borderColor: 'divider', flexShrink: 0 }} />
                  {editable ? (
                    <TextField value={opt} onChange={(e) => handleOptionChange(i, e.target.value)} size="small" fullWidth variant="standard"
                      InputProps={{ disableUnderline: true, sx: { fontSize: 14 } }}
                      placeholder={`Option ${i + 1}`} />
                  ) : (
                    <Typography sx={{ fontSize: 14 }}>{opt}</Typography>
                  )}
                  {editable && options.length > 1 && (
                    <IconButton size="small" onClick={() => removeOption(i)} sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}>
                      <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
                </Stack>
              ))}
              {editable && (
                <Button size="small" startIcon={<AddIcon sx={{ fontSize: 14 }} />} onClick={addOption}
                  sx={{ fontSize: 12, fontWeight: 600, color: 'primary.main', alignSelf: 'flex-start' }}>
                  Add option
                </Button>
              )}
            </Stack>
          )}

          {/* Condition editor */}
          {editable && !isInstruction && (
            showCondition ? (
              <ConditionEditor
                item={item}
                allItems={allTemplateItems.filter((i) => i.id !== item.id && i.type !== 'instruction')}
                onSave={(condition) => { autoSave({ condition }); setShowCondition(!!condition) }}
                onRemove={() => { autoSave({ condition: null }); setShowCondition(false) }}
              />
            ) : (
              <Button size="small" startIcon={<AccountTreeOutlinedIcon sx={{ fontSize: 14 }} />}
                onClick={() => setShowCondition(true)}
                sx={{ mt: 1.5, fontSize: 12, fontWeight: 600, color: 'text.secondary', textTransform: 'none' }}>
                Add condition
              </Button>
            )
          )}

          {!editable && item.condition && (
            <ConditionDisplay condition={item.condition} allItems={allTemplateItems} />
          )}
        </Box>

        {editable && (
          <Tooltip title="Delete this item" arrow>
            <IconButton size="small" onClick={onDelete} sx={{ color: 'text.disabled', mt: 0.5, '&:hover': { color: 'error.main' } }}>
              <DeleteOutlineIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </Box>
  )
}

// ═══════════════════════════════════════════════════════════════════
// CONDITION EDITOR + DISPLAY
// ═══════════════════════════════════════════════════════════════════

const OPERATORS: { value: string; label: string }[] = [
  { value: 'equals', label: 'is' },
  { value: 'not_equals', label: 'is not' },
  { value: 'contains', label: 'contains' },
  { value: 'gte', label: 'is at least' },
  { value: 'lte', label: 'is at most' },
]

function getValueOptions(item: ChecklistItem | undefined): string[] | null {
  if (!item) return null
  if (item.type === 'yes_no') return ['yes', 'no']
  if (item.type === 'pass_fail') return ['pass', 'fail']
  if (item.type === 'multiple_choice' || item.type === 'checkbox') return item.options ?? []
  return null
}

function ConditionEditor({ item, allItems, onSave, onRemove }: {
  item: ChecklistItem; allItems: ChecklistItem[]
  onSave: (condition: any) => void; onRemove: () => void
}) {
  const existing = item.condition
  const [targetId, setTargetId] = useState(existing?.item_id ?? '')
  const [operator, setOperator] = useState<string>(existing?.operator ?? 'equals')
  const [value, setValue] = useState(existing?.value ?? '')

  const targetItem = allItems.find((i) => i.id === targetId)
  const valueOptions = getValueOptions(targetItem)

  function handleSave() {
    if (!targetId || !value.trim()) return
    onSave({ item_id: targetId, operator, value: value.trim() })
  }

  useEffect(() => {
    if (targetId && operator && value.trim()) handleSave()
  }, [targetId, operator, value])

  return (
    <Box sx={{ mt: 2, p: 2, borderRadius: 2, border: '1px solid', borderColor: (t) => alpha(t.palette.warning.main, 0.3),
      bgcolor: (t) => alpha(t.palette.warning.main, 0.04) }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <AccountTreeOutlinedIcon sx={{ fontSize: 16, color: 'warning.main' }} />
          <Typography sx={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'warning.main' }}>
            Condition
          </Typography>
        </Stack>
        <Tooltip title="Remove condition" arrow>
          <IconButton size="small" onClick={onRemove} sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}>
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Stack>

      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary' }}>Show this item if</Typography>

        {/* Target question */}
        <Box sx={{ minWidth: 180 }}>
          <SearchableSelect
            value={targetId}
            onChange={(v) => { setTargetId(v); setValue('') }}
            options={allItems}
            getValue={(i) => i.id}
            getLabel={(i) => i.label || `(${i.type})`}
            placeholder="Select a question"
            renderOption={(i) => (
              <Typography noWrap sx={{ maxWidth: 250, fontSize: 12.5 }}>{i.label || `(${i.type})`}</Typography>
            )}
          />
        </Box>

        {/* Operator */}
        <Select value={operator} onChange={(e) => setOperator(e.target.value)} size="small"
          sx={{ minWidth: 100, fontSize: 12.5, height: 32, borderRadius: 1.5 }}>
          {OPERATORS.map((o) => (
            <MenuItem key={o.value} value={o.value} sx={{ fontSize: 12.5 }}>{o.label}</MenuItem>
          ))}
        </Select>

        {/* Value */}
        {valueOptions ? (
          <Select value={value} onChange={(e) => setValue(e.target.value)} size="small" displayEmpty
            sx={{ minWidth: 120, fontSize: 12.5, height: 32, borderRadius: 1.5 }}>
            <MenuItem value="" sx={{ fontSize: 12.5, color: 'text.secondary' }}>Select value</MenuItem>
            {valueOptions.map((v) => (
              <MenuItem key={v} value={v} sx={{ fontSize: 12.5 }}>{v.charAt(0).toUpperCase() + v.slice(1)}</MenuItem>
            ))}
          </Select>
        ) : (
          <TextField value={value} onChange={(e) => setValue(e.target.value)} size="small" placeholder="Value..."
            sx={{ minWidth: 120, '& .MuiOutlinedInput-root': { fontSize: 12.5, height: 32, borderRadius: 1.5 } }} />
        )}
      </Stack>
    </Box>
  )
}

function ConditionDisplay({ condition, allItems }: { condition: any; allItems: ChecklistItem[] }) {
  const target = allItems.find((i) => i.id === condition.item_id)
  const opLabel = OPERATORS.find((o) => o.value === condition.operator)?.label ?? condition.operator

  return (
    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 1.5, px: 1.5, py: 0.75, borderRadius: 1.5,
      bgcolor: (t) => alpha(t.palette.warning.main, 0.06), border: '1px solid', borderColor: (t) => alpha(t.palette.warning.main, 0.15) }}>
      <AccountTreeOutlinedIcon sx={{ fontSize: 14, color: 'warning.main' }} />
      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
        Show if <strong>"{target?.label || 'Unknown'}"</strong> {opLabel} <strong>"{condition.value}"</strong>
      </Typography>
    </Stack>
  )
}

// ═══════════════════════════════════════════════════════════════════
// PREVIEW VIEW — how a reporter would see this checklist
// ═══════════════════════════════════════════════════════════════════

function PreviewView({ template }: { template: ChecklistTemplate }) {
  return (
    <Box sx={{ px: 3, py: 4, maxWidth: 700, mx: 'auto' }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography sx={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em' }}>{template.name}</Typography>
        {template.description && <Typography sx={{ fontSize: 14, color: 'text.secondary', mt: 0.5 }}>{template.description}</Typography>}
        <Typography sx={{ fontSize: 12, color: 'text.disabled', mt: 1 }}>Preview — this is how the checklist will appear to the person filling it in</Typography>
      </Box>

      {template.sections.map((section) => (
        <Box key={section.id} sx={{ mb: 4 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 700, mb: 2, pb: 1, borderBottom: '2px solid', borderColor: 'primary.main' }}>
            {section.title}
          </Typography>

          <Stack spacing={2.5}>
            {section.items.map((item) => (
              <PreviewItem key={item.id} item={item} />
            ))}
          </Stack>

          {section.items.length === 0 && (
            <Typography sx={{ fontSize: 13, color: 'text.disabled', fontStyle: 'italic', py: 2 }}>No items in this section</Typography>
          )}
        </Box>
      ))}

      {template.sections.length === 0 && (
        <Box sx={{ py: 8, textAlign: 'center' }}>
          <Typography sx={{ fontSize: 15, color: 'text.secondary' }}>No sections added yet</Typography>
        </Box>
      )}
    </Box>
  )
}

function PreviewItem({ item }: { item: ChecklistItem }) {
  const isInstruction = item.type === 'instruction'

  if (isInstruction) {
    return (
      <Box sx={{ px: 2.5, py: 2, borderRadius: 2, bgcolor: (t) => alpha(t.palette.info.main, 0.06), border: '1px solid', borderColor: (t) => alpha(t.palette.info.main, 0.15) }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
          <InfoOutlinedIcon sx={{ fontSize: 16, color: 'info.main' }} />
          <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'info.main' }}>Instruction</Typography>
        </Stack>
        <Typography sx={{ fontSize: 14, fontStyle: 'italic', color: 'text.secondary' }}>{item.label}</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <Typography sx={{ fontSize: 14.5, fontWeight: 600, flex: 1 }}>
          {item.label || '(Untitled question)'}
          {item.required && <Typography component="span" sx={{ color: 'error.main', ml: 0.5 }}>*</Typography>}
        </Typography>
      </Stack>

      {item.type === 'text' && (
        <TextField size="small" fullWidth placeholder="Enter answer..." disabled
          sx={{ '& .MuiOutlinedInput-root': { fontSize: 14, bgcolor: 'action.hover' } }} />
      )}
      {item.type === 'number' && (
        <TextField size="small" type="number" placeholder="Enter number..." disabled sx={{ width: 200, '& .MuiOutlinedInput-root': { fontSize: 14, bgcolor: 'action.hover' } }} />
      )}
      {item.type === 'yes_no' && (
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" size="small" disabled sx={{ fontSize: 13, px: 3 }}>Yes</Button>
          <Button variant="outlined" size="small" disabled sx={{ fontSize: 13, px: 3 }}>No</Button>
        </Stack>
      )}
      {item.type === 'pass_fail' && (
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" size="small" disabled sx={{ fontSize: 13, px: 3, color: '#059669', borderColor: '#059669' }}>Pass</Button>
          <Button variant="outlined" size="small" disabled sx={{ fontSize: 13, px: 3, color: '#DC2626', borderColor: '#DC2626' }}>Fail</Button>
        </Stack>
      )}
      {item.type === 'multiple_choice' && (
        <Stack spacing={1}>
          {(item.options ?? []).map((opt, i) => (
            <Stack key={i} direction="row" alignItems="center" spacing={1.5}>
              <Box sx={{ width: 18, height: 18, borderRadius: 99, border: '2px solid', borderColor: 'divider' }} />
              <Typography sx={{ fontSize: 14 }}>{opt}</Typography>
            </Stack>
          ))}
        </Stack>
      )}
      {item.type === 'checkbox' && (
        <Stack spacing={1}>
          {(item.options ?? []).map((opt, i) => (
            <Stack key={i} direction="row" alignItems="center" spacing={1.5}>
              <Box sx={{ width: 18, height: 18, borderRadius: 0.5, border: '2px solid', borderColor: 'divider' }} />
              <Typography sx={{ fontSize: 14 }}>{opt}</Typography>
            </Stack>
          ))}
        </Stack>
      )}
      {item.type === 'date' && (
        <TextField size="small" type="date" disabled sx={{ width: 200, '& .MuiOutlinedInput-root': { fontSize: 14, bgcolor: 'action.hover' } }}
          InputLabelProps={{ shrink: true }} />
      )}
      {item.type === 'rating' && (
        <Rating value={0} disabled size="large" />
      )}
    </Box>
  )
}

// ═══════════════════════════════════════════════════════════════════
// HISTORY VIEW
// ═══════════════════════════════════════════════════════════════════

const ACTION_META: Record<string, { icon: React.ReactNode; color: string }> = {
  created:         { icon: <AddCircleOutlineIcon />,      color: '#3B82F6' },
  updated:         { icon: <EditOutlinedIcon />,           color: '#8B5CF6' },
  published:       { icon: <PublishOutlinedIcon />,        color: '#059669' },
  archived:        { icon: <DeleteOutlineIcon />,          color: '#94A3B8' },
  restored:        { icon: <AddCircleOutlineIcon />,       color: '#0891B2' },
  versioned:       { icon: <EditOutlinedIcon />,           color: '#D97706' },
  section_added:   { icon: <AddIcon />,                    color: '#059669' },
  section_updated: { icon: <EditOutlinedIcon />,           color: '#8B5CF6' },
  section_deleted: { icon: <DeleteOutlineIcon />,          color: '#DC2626' },
  item_added:      { icon: <AddIcon />,                    color: '#059669' },
  item_updated:    { icon: <EditOutlinedIcon />,           color: '#8B5CF6' },
  item_deleted:    { icon: <DeleteOutlineIcon />,          color: '#DC2626' },
  reordered:       { icon: <DragIndicatorIcon />,          color: '#D97706' },
}

function HistoryView({ template }: { template: ChecklistTemplate }) {
  const { formatDate } = useOrgSettings()

  const auditQ = useQuery({
    queryKey: ['checklist-audit', template.id],
    queryFn: () => api.checklists.getAuditLog(template.id),
  })
  const auditLog = Array.isArray(auditQ.data) ? auditQ.data : []

  return (
    <Box sx={{ px: 3, py: 4, maxWidth: 650, mx: 'auto' }}>
      {/* Details card */}
      <Box sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', mb: 3 }}>
        <Typography sx={{ fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', mb: 1.5 }}>Details</Typography>
        <Stack spacing={1.25}>
          <DetailRow label="Status" value={template.status.charAt(0).toUpperCase() + template.status.slice(1)} />
          <DetailRow label="Version" value={`v${template.version}`} />
          <DetailRow label="Sections" value={String(template.sections.length)} />
          <DetailRow label="Items" value={String(template.sections.reduce((s, sec) => s + sec.items.length, 0))} />
          {template.created_by_name && <DetailRow label="Created by" value={template.created_by_name} />}
          <DetailRow label="Created" value={formatDate(template.created_at)} />
          <DetailRow label="Last updated" value={formatDate(template.updated_at)} />
        </Stack>
      </Box>

      {template.tags.length > 0 && (
        <Box sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', mb: 3 }}>
          <Typography sx={{ fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', mb: 1.5 }}>Tags</Typography>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {template.tags.map((tag) => (
              <Chip key={tag.id} label={tag.name} size="small"
                sx={{ height: 24, fontSize: 12, fontWeight: 600, bgcolor: `${tag.color}18`, color: tag.color }} />
            ))}
          </Stack>
        </Box>
      )}

      {/* Activity log */}
      <Typography sx={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', mb: 2 }}>
        Activity log
      </Typography>

      {auditQ.isLoading && <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={22} /></Box>}

      {!auditQ.isLoading && auditLog.length === 0 && (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography sx={{ fontSize: 13, color: 'text.disabled' }}>No activity recorded yet</Typography>
        </Box>
      )}

      <Stack spacing={0}>
        {auditLog.map((entry: any, i: number) => {
          const meta = ACTION_META[entry.action] ?? { icon: <EditOutlinedIcon />, color: '#64748B' }
          return (
            <Box key={entry.id} sx={{ borderBottom: i < auditLog.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
              <Stack direction="row" alignItems="flex-start" spacing={2}
                sx={{ py: 2, px: 1.5, mx: -1.5, borderRadius: 2, transition: 'background-color 0.15s',
                  '&:hover': { bgcolor: (t) => alpha(t.palette.divider, 0.06) } }}>
                <Box sx={{ color: alpha(meta.color, 0.7), mt: 0.25, flexShrink: 0, '& svg': { fontSize: 18 } }}>
                  {meta.icon}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 600, lineHeight: 1.35 }}>
                    {entry.details || entry.action.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}
                  </Typography>
                  {entry.user_name && (
                    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.5 }}>
                      <Box sx={{ width: 18, height: 18, borderRadius: '50%', bgcolor: meta.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography sx={{ fontSize: 8, fontWeight: 700, color: '#fff' }}>
                          {entry.user_name.split(' ').map((w: string) => w[0]).join('').toUpperCase().slice(0, 2)}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{entry.user_name}</Typography>
                    </Stack>
                  )}
                </Box>
                <Typography sx={{ fontSize: 12, color: 'text.disabled', flexShrink: 0, whiteSpace: 'nowrap' }}>
                  {formatDate(entry.created_at, true)}
                </Typography>
              </Stack>
            </Box>
          )
        })}
      </Stack>
    </Box>
  )
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between">
      <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{label}</Typography>
      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{value}</Typography>
    </Stack>
  )
}
